import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { romanLabel, splitOptions } from "../create/paperBuilder";

// Inline markdown for question text (bold/italic/code) — p mapped to span to stay inline.
const Md = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <span>{children}</span>,
      code: ({ children }) => <code style={{ background: "#eef1f5", borderRadius: 4, padding: "0 4px", fontFamily: "monospace" }}>{children}</code>,
    }}
  >
    {text || ""}
  </ReactMarkdown>
);

const subRowStyle = { display: "flex", gap: "10px", marginBottom: "4px" };
const qImgStyle = { display: "block", maxWidth: "100%", maxHeight: "260px", margin: "6px 0", objectFit: "contain" };

// Image attached to a question / sub-question, if any.
const QImage = ({ src }) => (src ? <img src={src} alt="" className="qp-qimg" style={qImgStyle} /> : null);

// MCQ options block (newline-separated -> lines).
const Options = ({ options, pl = "0" }) => {
  const opts = splitOptions(options);
  if (!opts.length) return null;
  return (
    <div style={{ paddingLeft: pl, marginTop: "2px", fontSize: "11pt" }}>
      {opts.map((o, i) => (
        <div key={i} style={{ whiteSpace: "pre-wrap" }}>{o}</div>
      ))}
    </div>
  );
};

// PART A — numbered list of short-answer questions. A question may be a plain string,
// or a split slot ({ subs: [{ text, marks }] }) rendered as i) / ii) sub-questions.
const PartA = ({ part }) => {
  if (!part) return null;
  return (
    <section className="qp-part">
      <div className="qp-part-head">
        <h3>{part.title}</h3>
        <span className="qp-marks">{part.marksLine}</span>
      </div>
      {part.instructions?.length > 0 && (
        <ul className="qp-instructions">
          {part.instructions.map((ins, idx) => (
            <li key={idx}>{ins}</li>
          ))}
        </ul>
      )}
      <ol className="qp-qlist" start={part.start || 1}>
        {part.questions?.map((q, idx) => (
          <li key={idx}>
            {q && q.subs ? (
              <div>
                {q.subs.map((sub, si) => (
                  <div key={si}>
                    <div style={subRowStyle}>
                      <span style={{ fontStyle: "italic", minWidth: "26px" }}>{romanLabel(si)})</span>
                      <span style={{ flex: 1 }}><Md text={sub.text} /></span>
                      <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>({sub.marks})</span>
                    </div>
                    <QImage src={sub.image} />
                    <Options options={sub.options} pl="36px" />
                  </div>
                ))}
              </div>
            ) : typeof q === "string" ? (
              <Md text={q} />
            ) : (
              <>
                <div style={subRowStyle}>
                  <span style={{ flex: 1 }}><Md text={q?.text} /></span>
                  {q?.marks != null && (
                    <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>({q.marks})</span>
                  )}
                </div>
                <QImage src={q?.image} />
                <Options options={q?.options} />
              </>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};

export default PartA;
